import { NATIVE_MINT } from "@solana/spl-token";
import { LAMPORTS_PER_SOL, PublicKey } from "@solana/web3.js";
import {
  EMPTY_STRING,
  MAX_VOTING_POWER,
} from "../../common/constants/common.constants";
import {
  EXECUTE_TRANSFER_PROPOSAL,
  EXECUTE_WITHDRAWAL_FEE,
  TREASURY_RENT,
} from "../../common/constants/fees";
import {
  CreateProposalActiveStep,
  GoveranceType,
  ProposalAction,
  ProposalBuyOption,
} from "../../common/enums/proposals.enums";
import { IClubTreasury, ITreasuryTokenAccInfo } from "../../common/interfaces/clubs.interface";
import {
  ProposalOption,
  ProposalTypeNew,
} from "../../common/interfaces/proposals.interface";
import { round } from "../../utilities/helpers";

export const validateCreateProposal = async (
  values,
  activeStep,
  treasury: IClubTreasury
) => {
  const errors: any = {};

  if (activeStep === CreateProposalActiveStep.ProposalValues) {
    if (values.proposalTitle === EMPTY_STRING) {
      errors.proposalTitle = "Proposal title cannot be empty!";
    }

    if (Buffer.from(values.proposalTitle).byteLength > 64) {
      errors.proposalTitle =
        "Proposal title length cannot exceed 64 characters";
    }

    if (values.proposalDescription === EMPTY_STRING) {
      errors.proposalDescription = "Proposal description cannot be empty!";
    }
    if (
      values.proposalOption === ProposalOption.buyNft &&
      (values.isNftMintValid === false || values.nftToBuy.length < 32) &&
      values.proposalType === ProposalTypeNew.TreasuryVault
    ) {
      switch (values.buyOption) {
        case ProposalBuyOption.MagicEden:
          errors.nftToBuy = "Please enter valid Magic Eden link";
          break;
        case ProposalBuyOption.PeerToPeer:
          errors.nftToBuy = "Please enter valid NFT mint";
          break;
        case ProposalBuyOption.Solsea:
          errors.nftToBuy = "Please enter valid Solsea link";
          break;
        default:
          errors.nftToBuy = "Not supported buy option!";
      }
    }

    if (
      values.proposalOption === ProposalOption.sellNft &&
      values.nft === null
    ) {
      errors.nft = "Please select NFT to sell!";
    }

    if (
      values.proposalType === ProposalTypeNew.TreasuryVault &&
      (values.price < 0 || values.price === EMPTY_STRING)
    ) {
      errors.price = "NFT price cannot be empty or less than zero!";
    }

    if (
      values.buyOption === ProposalBuyOption.MagicEden &&
      values.price === 0
    ) {
      errors.price = "NFT can't be listed for 0 SOL's on Magic Eden!";
    }

    if (
      values.proposalType === ProposalTypeNew.Discussion ||
      values.proposalOption === ProposalAction.BuyNft
    ) {
      if (values.link !== EMPTY_STRING && !values.link.startsWith("http")) {
        errors.link = "Please enter valid link";
      }
    }

    if (values.proposalType === ProposalTypeNew.Withdrawal) {
      if (values.withdrawAmount === EMPTY_STRING) {
        errors.withdrawAmount = "Withdraw amount can not be empty";
      }

      if (
        values.withdrawAmount !== EMPTY_STRING &&
        values.withdrawAmount <= 0
      ) {
        errors.withdrawAmount = "Amount can not be zero or less then zero";
      }
      if (values.withdrawAmount !== EMPTY_STRING) {
        if (
          values.mint.toString() === NATIVE_MINT.toString() &&
          treasury.denominatedTokenInfo === undefined
        ) {
          if (
            values.withdrawAmount >
            treasury.treasuryAmount - EXECUTE_WITHDRAWAL_FEE
          ) {
            errors.withdrawAmount =
              "You do not have enough funds to create this withdrawal proposal";
          }
        } else {
          // Select treasury token account based on selection or mint
          let selectedToken: ITreasuryTokenAccInfo | undefined = undefined;
          if (values.tokenAddress) {
            selectedToken = treasury.tokenAccounts.find(
              (item) => item.tokenAddress?.toString() === values.tokenAddress
            );
          }
          if (!selectedToken) {
            selectedToken = treasury.tokenAccounts.find(
              (item) => item.mint.toString() === values.mint.toString()
            );
          }
          if (selectedToken && selectedToken.balance < values.withdrawAmount) {
            errors.withdrawAmount =
              "You do not have enough funds to create this withdrawal proposal";
          }
        }
      }
    }

    if (values.proposalType === ProposalTypeNew.Transfer) {
      if (values.amountToInvest === EMPTY_STRING) {
        errors.amountToInvest = "Amount can not be empty";
      }

      if (
        values.amountToInvest !== EMPTY_STRING &&
        values.amountToInvest <= 0
      ) {
        errors.amountToInvest = "Amount can not be zero or less then zero";
      }
      if (values.amountToInvest !== EMPTY_STRING) {
        // Select treasury token account based on selection or mint
        let selectedToken: ITreasuryTokenAccInfo | undefined = undefined;
        if (values.tokenAddress) {
          selectedToken = treasury.tokenAccounts.find(
            (item) => item.tokenAddress?.toString() === values.tokenAddress
          );
        }
        if (!selectedToken) {
          selectedToken = treasury.tokenAccounts.find(
            (item) => item.mint.toString() === values.mint.toString()
          );
        }
        if (selectedToken) {
          const nativeTransferWithoutDenomCurrency =
            selectedToken.mint.toString() === NATIVE_MINT.toString() &&
            treasury.denominatedTokenInfo === undefined;
          const tokenBalance = nativeTransferWithoutDenomCurrency
            ? round(
                selectedToken.balance -
                  EXECUTE_TRANSFER_PROPOSAL -
                  TREASURY_RENT
              )
            : selectedToken.balance;
          if (tokenBalance < values.amountToInvest) {
            errors.amountToInvest = `You do not have enough funds to create this proposal. Maximum amount is ${
              tokenBalance > 0 ? tokenBalance : 0
            }`;
          }
        }
      }

      if (values.transferAddress === EMPTY_STRING) {
        errors.transferAddress = "Transfer address can not be empty";
      }

      if (values.transferAddress !== EMPTY_STRING) {
        try {
          const publicKey = new PublicKey(values.transferAddress);
        } catch (error) {
          errors.transferAddress = "Public key is not valid";
        }
      }
    }
    if (
      values.proposalType === ProposalTypeNew.UpdateClubConfig ||
      values.proposalType === ProposalTypeNew.AddSellPermission
    ) {
      if (values.proposalOption === ProposalAction.UpdateGovernanceConfig) {
        if (values.newApprovalQuorum && values.newApprovalQuorum.length === 0) {
          errors.newApprovalQuorum = "New approval quourm cannot be empty!";
        }

        if (
          values.newVotingTime === undefined ||
          values.newVotingTime === EMPTY_STRING
        ) {
          errors.newVotingTime = "New voting time cannot be empty";
        }

        if (
          values.newVotingTime !== undefined &&
          values.newVotingTime !== EMPTY_STRING
        ) {
          if (values.newVotingTime < 1 || values.newVotingTime > 30) {
            errors.newVotingTime =
              "New voting time cannot be less than 1 and greater than 30 days!";
          }
          if (
            values.newVotingTime &&
            (values.newVotingTime.toString().includes(".") ||
              values.newVotingTime.toString().includes(","))
          ) {
            errors.transferMaxVoteTime = "Max voting time can not be decimal";
          }
        }
      }
      if (
        values.proposalOption === ProposalAction.UpdateGovernanceConfig ||
        values.proposalOption === ProposalAction.AddSellPermission
      ) {
        if (!values.governanceType) {
          errors.governanceType = "Governance type to update must be selected!";
        }
        if (
          values.governanceType &&
          values.governanceType.type !== GoveranceType.TradeApprovalGovernance
        ) {
          if (
            (values.newApprovalQuorum === EMPTY_STRING ||
              !values.newApprovalQuorum) &&
            values.newApprovalQuorum !== 0
          ) {
            errors.newApprovalQuorum = "New approval quorum can not be empty!";
          }
          if (
            values.governanceType.type === GoveranceType.DefaultGovernance &&
            values.newApprovalQuorum < 51
          ) {
            errors.newApprovalQuorum =
              "New approval quorum can not be less than 51";
          }
          if (values.newApprovalQuorum > 100) {
            errors.newApprovalQuorum =
              "New approval quorum can not be greated than 100";
          }
        } else {
          if (!values.governanceAccounts) return errors;
          const activeGovernance = values.governanceAccounts.findIndex(
            (acc) => acc.isChecked
          );

          if (activeGovernance < 0) return errors;

          if (
            values.governanceAccounts[activeGovernance].newQuorum < 0 ||
            values.governanceAccounts[activeGovernance].newQuorum > 100
          ) {
            errors.governanceAccounts =
              "Quorum can't be less than 0 and greater than 100%";
          }
          if (!values.activeTradeApprovalQuorum) {
            errors.activeTradeApprovalQuorum = "Please choose trade approval!";
          }
        }
      }
      if (values.proposalOption === ProposalAction.UpdateRoleConfig) {
        if (values.roleVotes.length === 0) {
          errors.roleVotes = "Role votes can not be empty";
        }
        const emptySum = values.roleVotes.filter(
          (r) => r.updateWeight === EMPTY_STRING
        );
        if (emptySum.length === values.roleVotes.length) {
          errors.roleVotes = "Updated role weight cannot be empty!";
        }

        if (values.proposalOption === ProposalAction.UpdateRoleConfig) {
          values.roleVotes.forEach((roleVote, index) => {
            if (
              roleVote.updateWeight !== EMPTY_STRING &&
              (roleVote.updateWeight < 1 ||
                roleVote.updateWeight > MAX_VOTING_POWER)
            ) {
              errors.roleVotes =
                "Role weight can not be less than 1 and greater than million";
            }
          });
        }
      }
    }
    if (
      values.proposalType === ProposalTypeNew.AddSellPermission &&
      values.governanceAccounts
    ) {
      values.governanceAccounts.map((configuration, index) => {
        if (
          configuration.isChecked &&
          (configuration.from < 0 || configuration.from === "")
        ) {
          errors.governanceAccounts =
            "Value from can't be empty or less than zero.";
        }
        if (configuration.to < 0 || configuration.to === "") {
          errors.governanceAccounts =
            "Value to can't be empty or less than zero.";
        }
        if (
          configuration.isChecked &&
          (configuration.newQuorum === "" ||
            configuration.newQuorum === undefined)
        ) {
          errors.governanceAccounts = "Value at least can't be empty";
        }

        if (
          configuration.from &&
          configuration.from !== "" &&
          configuration.to &&
          configuration.to !== "" &&
          configuration.from >= configuration.to &&
          configuration.isChecked
        ) {
          errors.governanceAccounts =
            "Value from has to be less than value to.";
        }

        if (
          configuration.newQuorum &&
          configuration.newQuorum !== "" &&
          configuration.isChecked &&
          (configuration.newQuorum.toString().includes(".") ||
            configuration.newQuorum.toString().includes(","))
        ) {
          errors.governanceAccounts = "Value can not be decimal";
        }

        if (
          configuration.newQuorum &&
          configuration.newQuorum !== "" &&
          configuration.isChecked &&
          (configuration.newQuorum < 0 || configuration.newQuorum > 100)
        ) {
          errors.governanceAccounts =
            "Value at least can't be less then 0 or greater then 100";
        }

        if (
          configuration.from &&
          configuration.from !== EMPTY_STRING &&
          configuration.to &&
          configuration.isChecked &&
          configuration.to !== EMPTY_STRING &&
          values.governanceAccounts.find(
            (item, itemIndex) =>
              index !== itemIndex &&
              item.from &&
              item.from !== EMPTY_STRING &&
              item.to &&
              item.to !== EMPTY_STRING &&
              configuration.from < item.to &&
              configuration.to > item.from
          )
        ) {
          errors.governanceAccounts =
            "You have already set values for this range";
          errors.governanceAccounts =
            "You have already set values for this range";
          return errors;
        }
      });
    }
  }

  return errors;
};
